/**
 * The scratch-off foil: a canvas laid over the prize that the dealer rubs away
 * with a finger. Pointer events (so mouse, pen and touch all work in the
 * Capacitor WebView and in a browser), `touch-action: none` so the page never
 * scrolls mid-scratch, and pointer capture so a stroke that leaves the card
 * keeps erasing until the finger lifts.
 *
 * Progress is measured by sampling the canvas' alpha channel on a coarse grid
 * (every 6th pixel — ~2k samples, negligible) at the end of each stroke. Once
 * the cleared share passes SCRATCH_REVEAL_THRESHOLD the remaining foil fades
 * out and `onReveal` fires exactly once — nobody should have to scrape every
 * last corner.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { SCRATCH_REVEAL_THRESHOLD } from '@mitang/shared';

const BRUSH_RADIUS = 24; // CSS px
const SAMPLE_STEP = 6;

function paintFoil(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  // Brushed-silver gradient with faint diagonal stripes, then the prompt text.
  const g = ctx.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, '#d7dae1');
  g.addColorStop(0.35, '#f1f3f7');
  g.addColorStop(0.6, '#c4c8d2');
  g.addColorStop(1, '#e6e8ee');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  ctx.save();
  ctx.globalAlpha = 0.18;
  ctx.strokeStyle = '#8f95a3';
  ctx.lineWidth = 6;
  for (let x = -h; x < w + h; x += 26) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x + h, h);
    ctx.stroke();
  }
  ctx.restore();

  ctx.fillStyle = '#4b5261';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = '800 15px Inter, system-ui, sans-serif';
  ctx.fillText('SCRATCH HERE', w / 2, h / 2 - 12);
  ctx.font = '600 11.5px Inter, system-ui, sans-serif';
  ctx.fillStyle = '#6b7280';
  ctx.fillText('Rub the card with your finger to reveal your prize', w / 2, h / 2 + 12);
}

export function ScratchSurface({
  children,
  onReveal,
}: {
  /** The prize face, rendered underneath the foil. */
  children: ReactNode;
  /** Fires once, when enough foil is gone. */
  onReveal: () => void;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const scratching = useRef(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const revealedRef = useRef(false);
  const [revealed, setRevealed] = useState(false);
  const [touched, setTouched] = useState(false);

  // Size the canvas to the card (device-pixel aware) and paint the foil once.
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const rect = wrap.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    canvas.width = Math.max(1, Math.round(rect.width * dpr));
    canvas.height = Math.max(1, Math.round(rect.height * dpr));
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    paintFoil(ctx, rect.width, rect.height);
  }, []);

  const clearedShare = useCallback((): number => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return 0;
    const { width, height } = canvas;
    const data = ctx.getImageData(0, 0, width, height).data;
    let cleared = 0;
    let total = 0;
    for (let y = 0; y < height; y += SAMPLE_STEP) {
      for (let x = 0; x < width; x += SAMPLE_STEP) {
        total++;
        if (data[(y * width + x) * 4 + 3] === 0) cleared++;
      }
    }
    return total === 0 ? 0 : cleared / total;
  }, []);

  const finish = useCallback(() => {
    if (revealedRef.current) return;
    revealedRef.current = true;
    setRevealed(true);
    onReveal();
  }, [onReveal]);

  const eraseAt = useCallback((x: number, y: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = BRUSH_RADIUS * 2;
    const prev = lastPoint.current;
    ctx.beginPath();
    if (prev) {
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(x, y);
      ctx.stroke();
    } else {
      ctx.arc(x, y, BRUSH_RADIUS, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    lastPoint.current = { x, y };
  }, []);

  const pointFrom = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (revealedRef.current) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    scratching.current = true;
    lastPoint.current = null;
    setTouched(true);
    const p = pointFrom(e);
    eraseAt(p.x, p.y);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!scratching.current || revealedRef.current) return;
    e.preventDefault();
    const p = pointFrom(e);
    eraseAt(p.x, p.y);
  };

  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!scratching.current) return;
    scratching.current = false;
    lastPoint.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    if (clearedShare() >= SCRATCH_REVEAL_THRESHOLD) finish();
  };

  return (
    <div ref={wrapRef} className="relative aspect-[16/10] w-full select-none overflow-hidden rounded-2xl">
      <div className="absolute inset-0">{children}</div>
      <canvas
        ref={canvasRef}
        aria-label="Scratch area"
        role="img"
        className={`absolute inset-0 rounded-2xl transition-opacity duration-700 ${
          revealed ? 'pointer-events-none opacity-0' : 'opacity-100'
        }`}
        style={{ touchAction: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={(e) => {
          // Capture keeps the stroke alive off-card; only end it if capture was lost.
          if (!e.currentTarget.hasPointerCapture(e.pointerId)) onPointerUp(e);
        }}
      />
      {!touched && !revealed ? (
        <div className="pointer-events-none absolute bottom-3 left-0 right-0 flex justify-center">
          <span className="animate-bounce rounded-full bg-white/80 px-3 py-1 text-[11px] font-bold text-slate-600 shadow-mini">
            👆 Start scratching
          </span>
        </div>
      ) : null}
    </div>
  );
}
